import React, { useState } from "react";
import { useGov } from "@/hooks/governance";
import { govActionLabel, govStatusTone } from "@/lib/governance";
import { Badge, HashCell } from "@/components/primitives";
import { Table } from "@/components/Table";
import { GovPageShell, GovError, GovPager, useGovNav } from "@/components/governance/GovShell";

const TYPES = [
  { value: "", label: "All types" },
  { value: "info_action", label: "Info Action" },
  { value: "parameter_change", label: "Parameter Change" },
  { value: "treasury_withdrawals", label: "Treasury Withdrawal" },
  { value: "hard_fork_initiation", label: "Hard Fork" },
  { value: "new_committee", label: "New Committee" },
  { value: "new_constitution", label: "New Constitution" },
  { value: "no_confidence", label: "No Confidence" },
];

const PAGE_SIZE = 20;

const columns = (navigate) => [
  {
    key: "type",
    label: "Type",
    render: (r) => (
      <button
        type="button"
        data-testid={`proposal-row-${r.tx_hash}-${r.cert_index}`}
        onClick={() => navigate(`/governance/proposals/${r.tx_hash}/${r.cert_index}`)}
        className="text-left text-foreground hover:text-primary underline decoration-dotted decoration-border underline-offset-2 transition-colors"
        title={r.title || r.gov_action_id}
      >
        {govActionLabel(r.type)}
      </button>
    ),
  },
  { key: "id", label: "Action ID", render: (r) => <HashCell value={r.gov_action_id} len={10} /> },
  { key: "status", label: "Status", render: (r) => <Badge tone={govStatusTone(r.status)}>{r.status}</Badge> },
  { key: "submitted", label: "Submitted", align: "right", render: (r) => <span className="text-muted-foreground">epoch {r.submitted_epoch ?? "—"}</span> },
  { key: "expires", label: "Expires", align: "right", render: (r) => <span className="text-muted-foreground">epoch {r.expires_after_epoch ?? "—"}</span> },
  {
    key: "outcome",
    label: "Outcome",
    align: "right",
    render: (r) =>
      r.enacted_epoch ? <span className="text-emerald-400">enacted {r.enacted_epoch}</span>
        : r.ratified_epoch ? <span className="text-emerald-400">ratified {r.ratified_epoch}</span>
          : r.expired_epoch ? <span className="text-muted-foreground">expired {r.expired_epoch}</span>
            : r.dropped_epoch ? <span className="text-red-400">dropped {r.dropped_epoch}</span>
              : <span className="text-muted-foreground">—</span>,
  },
];

export const ProposalsList = ({ meta, mode = "all" }) => {
  const navigate = useGovNav();
  const live = mode === "live";
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");

  const params = live
    ? undefined
    : { count: PAGE_SIZE, page, order: "desc", ...(type ? { type } : {}) };
  const { data, error, loading, refetch } = useGov(
    live ? "/governance/proposals/live" : "/governance/proposals",
    { params },
  );

  let rows = Array.isArray(data) ? data : [];
  if (live && type) rows = rows.filter((r) => r.type === type);

  const subtitle = live
    ? "Governance actions currently open for DRep, SPO & committee voting."
    : "Every governance action ever submitted on-chain, newest first.";

  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle={subtitle}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mono">
          Filter
          <select
            data-testid="proposal-type-filter"
            value={type}
            onChange={(e) => { setType(e.target.value); setPage(1); }}
            className="mono tracking-normal text-[12px] bg-card border border-primary/20 rounded px-2 py-1.5 text-foreground"
          >
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        {!live ? <span className="text-[10px] mono text-muted-foreground">{rows.length} shown</span> : null}
      </div>

      {error ? (
        <GovError error={error} onRetry={refetch} />
      ) : (
        <>
          <Table
            columns={columns(navigate)}
            rows={rows}
            loading={loading}
            skeletonRows={8}
            empty={live ? "No governance actions are open for voting right now." : "No governance actions found."}
          />
          {!live ? (
            <GovPager page={page} onPage={setPage} hasNext={rows.length === PAGE_SIZE} loading={loading} />
          ) : null}
        </>
      )}
    </GovPageShell>
  );
};
